import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { configureScope, init } from '@sentry/browser'
import googleAnalytics4 from './lib/googleAnalytics.js'
import configuration from './configuration.js'
import {
  adjustZoom,
  loadScript,
  getSponsorshipInfoVIZIO,
  getSponsorshipInfoTizen,
  getSponsorshipInfoComcast,
  platformStoreGet,
  platformStoreDelete,
  platformTitle,
  triggerSentryError,
} from '../../shared/helpers'
import {
  getStylesTizen,
  getStylesVIZIO,
  getStylesComcast,
} from '../../shared/captionsMapping.js'
import { store } from './store'
import {
  checkDeviceId,
  checkActivationStatus,
  deleteActivationStatus,
} from './actions/contentActions.js'
import tts from './lib/TTS.js'
import App from './App'
import './index.scss'
import {
  onKeyDown,
  registerTizenNumericalKeys,
  registerTizenMediaKeys,
} from '../../shared/keys/keyEvents'
import setupFocus from '../../shared/spatialNavigationFocus.js'
import Platform from './platforms/index.js'

setupFocus()

// If Sentry isn't defined, mock it so we don't have to check for it everywhere
if (typeof Sentry === 'undefined') {
  window.Sentry = {
    captureException: () => {
      console.log('mock Sentry.captureException')
    },
    configureScope: () => {
      console.log('mock Sentry.configureScope')
    },
    init: () => {
      console.log('mock Sentry.init')
    },
  }
}

if (BUILD_NUMBER) {
  // Set up Sentry
  const SENTRY_PUBLIC_DSN =
    configuration.sentryDsns[window.location.host] ||
    configuration.sentryDsns.defaults

  init({
    dsn: SENTRY_PUBLIC_DSN,
    maxBreadcrumbs: 50,
    ignoreErrors: [/.*Blocked a frame with origin.*/],
    normalizeDepth: 5,
    release: BUILD_NUMBER,
  })
  configureScope((scope) => {
    scope.setExtra('BUILD_NUMBER', BUILD_NUMBER)
    scope.setTag('BUILD_NUMBER', BUILD_NUMBER)
    scope.setTag('platformTitle', platformTitle())
  })
}

//Remove the autofocused element that we use as a workaround for Tizen TTS reading "Document Web"
try {
  document.getElementById('prevent_document_web').remove()
} catch (e) {
  // empty block
}

// Commenting out as this hasn't been used in a while but we still might want the token
// datadogLogs.init({
//   clientToken: 'pub62d3c26ce353104ea188723be3771788',
//   forwardErrorsToLogs: true,
//   sampleRate: 1, // only sample logs from 1% of users
// })

// If google analytics isn't defined, mock it so we don't have to check for it everywhere
/* if (typeof window.ga === 'undefined') {
  window.ga = () => {
    console.log('mock ga')
  }
} */

async function main() {
  platformStoreDelete('connectionMessage')
  window.forceCheckDebounce = null
  let captionsOn = platformStoreGet('captionsOn') === 'true'

  if (platformStoreGet('UPNEXT_ENABLED') === 'false') {
    store.dispatch({
      type: 'SET_UPNEXT_ENABLED',
      payload: false,
    })
  }

  Platform.getMessageInformation()

  if (COMPILING_FOR_TIZEN) {
    try {
      await loadScript('$WEBAPIS/webapis/webapis.js')
      // window.ga('create', configuration.analytics.samsungTrackingId, 'auto')
      // window.ga('set', 'appId', configuration.analytics.samsungAppId)

      // TODO: https://projects.pbs.org/jira/browse/OTTERKIDS-443
      // TODO: https://projects.pbs.org/jira/browse/OTTERGA-592
      // try {
      //   window.ga('set', 'dimension98', window.webapis.productinfo.getFirmware())
      //   window.ga('set', 'dimension99', window.webapis.productinfo.getRealModel())
      // } catch (e) {
      //   // TODO: set default values?
      // }

      registerTizenNumericalKeys()
      registerTizenMediaKeys()

      //Set up TTS
      const onVoiceGuideChange = () => {
        if (
          window.webapis.tvinfo.getMenuValue(
            window.webapis.tvinfo.TvInfoMenuKey.VOICE_GUIDE_KEY
          ) !== 0
        ) {
          store.dispatch({
            type: 'UPDATE_DEVICE',
            payload: {
              ttsEnabled: true,
            },
          })
        } else {
          store.dispatch({
            type: 'UPDATE_DEVICE',
            payload: {
              ttsEnabled: false,
            },
          })
        }
      }

      window.webapis.tvinfo.addCaptionChangeListener(
        window.webapis.tvinfo.TvInfoMenuKey.VOICE_GUIDE_KEY,
        onVoiceGuideChange
      )
      onVoiceGuideChange()

      try {
        window.webapis.tvinfo.registerInAppCaptionControl(true)
      } catch (error) {
        console.error(error)
      }
      // When launching on a Tizen TV, if the tv setting is on, captions are on.
      // If the tv setting is off, we look at localstorage.
      captionsOn =
        window.webapis.tvinfo.getMenuValue(
          window.webapis.tvinfo.TvInfoMenuKey.CAPTION_ONOFF_KEY
        ) === 1

      // get system caption style key values, preprocess them, and store them
      let mappedStyles = getStylesTizen()
      mappedStyles.enabled = captionsOn
      store.dispatch({
        type: 'SET_CAPTION_STATE',
        payload: mappedStyles,
      })

      window.webapis.tvinfo.showCaption(captionsOn)

      try {
        const deviceModel = window.webapis.productinfo.getModel()
        store.dispatch({
          type: 'UPDATE_DEVICE',
          payload: { deviceModel },
        })
      } catch (e) {
        store.dispatch({
          type: 'UPDATE_DEVICE',
          payload: { deviceModel: 'UNKNOWN TIZEN' },
        })
        console.error('error with window.webapis.productinfo.getModel()', e)
      }

      try {
        const ppid = await getSponsorshipInfoTizen()

        store.dispatch({
          type: 'UPDATE_DEVICE',
          payload: { ...ppid },
        })
      } catch (e) {
        triggerSentryError('Error configuring Tizen specific', 'index.js', {
          errorMessage: e.message ?? 'Undefined error message (default)',
          errorName: e.name ?? 'An error occurred (default)',
        })
        // store.dispatch({
        //   type: 'UPDATE_DEVICE',
        //   payload: { adsEnabled: false },
        // })
      }
    } catch (error) {
      triggerSentryError('Error configuring Tizen general', 'index.js', {
        errorName: error.name ?? 'An error occurred (default)',
        errorMessage: error.message ?? 'Undefined error message (default)',
      })
    }
  } else if (COMPILING_FOR_VIZIO) {
    await loadScript('http://localhost:12345/scfs/cl/js/vizio-companion-lib.js')

    await (async () => {
      return new Promise((res) => {
        document.addEventListener('VIZIO_LIBRARY_DID_LOAD', (e) => {
          res(e)
        })
      })
    })()

    adjustZoom()

    // window.ga('create', configuration.analytics.vizioTrackingId, 'auto')
    // window.ga('set', 'appId', configuration.analytics.vizioAppId)
    // TODO: https://projects.pbs.org/jira/browse/OTTERKIDS-443
    // TODO: https://projects.pbs.org/jira/browse/OTTERGA-592
    // try {
    //    window.VIZIO.getFirmwareVersion(function (firmwareVersion) {
    //     window.ga('set', 'dimension98', firmwareVersion)
    //   })
    //   window.ga(
    //     'set',
    //     'dimension99',
    //      window.VIZIO.deviceModel ?  window.VIZIO.deviceModel : 'Vizio'
    //   )
    // } catch (error) {
    //   // TODO: set default values?
    // }
    //set up TTS
    const vizioProperties = await window.VIZIO.getDeviceManifest()
    store.dispatch({
      type: 'UPDATE_DEVICE',
      payload: {
        ttsEnabled: vizioProperties.tts_enabled,
      },
    })
    document.addEventListener('VIZIO_TTS_DISABLED', () => {
      store.dispatch({
        type: 'UPDATE_DEVICE',
        payload: {
          ttsEnabled: false,
        },
      })
    })

    document.addEventListener('VIZIO_TTS_ENABLED', () => {
      store.dispatch({
        type: 'UPDATE_DEVICE',
        payload: {
          ttsEnabled: true,
        },
      })
    })

    const ppid = await getSponsorshipInfoVIZIO()
    store.dispatch({
      type: 'UPDATE_DEVICE',
      payload: { ...ppid },
    })

    window.VIZIO.setClosedCaptionHandler((enabled) => {
      store.dispatch({
        type: 'SET_CAPTION_STATE',
        payload: { enabled },
      })
    })
    window.VIZIO.setClosedCaptionStyleChangeHandler((value) => {
      let mappedStyles = getStylesVIZIO(value)
      try {
        store.dispatch({
          type: 'SET_CAPTION_STATE',
          payload: mappedStyles,
        })
      } catch (error) {
        console.error(error)
      }
    })

    const deviceModel = window.VIZIO.deviceModel

    store.dispatch({
      type: 'UPDATE_DEVICE',
      payload: { deviceModel },
    })
  } else if (COMPILING_FOR_COMCASTX1) {
    import('@firebolt-js/sdk').then(async function (firebolt) {
      const Accessibility = firebolt.Accessibility
      const Lifecycle = firebolt.Lifecycle
      const Discovery = firebolt.Discovery
      let timeOutId
      let listenerId
      listenerId = await Lifecycle.listen((event, { previous }) => {
        switch (event) {
          case Lifecycle.LifecycleState.UNLOADING:
            Discovery.clear(listenerId)
            Lifecycle.finished()
            break
          case Lifecycle.LifecycleState.INACTIVE:
            try {
              const fromStates = [
                Lifecycle.LifecycleState.BACKGROUND,
                Lifecycle.LifecycleState.FOREGROUND,
              ]
              if (fromStates.includes(previous)) {
                const href = window.location.href
                if (href.includes('/video-playback/')) {
                  const urlParams =
                    window.location.href.split('/video-playback/')
                  if (urlParams.length === 2) {
                    window.location.href = `${urlParams[0]}/video-details/${urlParams[1]}`
                  } else {
                    window.location.href = '/home'
                  }
                } else if (!href.includes('/home')) {
                  timeOutId = setTimeout(() => {
                    window.location.href = '/home'
                  }, 60 * 1000)
                }
              }
            } catch (error) {
              console.error(
                'Failed when trying to handle redirections in Comcast Lifecycle',
                error
              )
            }
            break
          case Lifecycle.LifecycleState.BACKGROUND:
          case Lifecycle.LifecycleState.FOREGROUND:
            if (previous === Lifecycle.LifecycleState.INACTIVE) {
              clearTimeout(timeOutId)
            }
            break
          default:
            break
        }
      })
      // window.ga('create', configuration.analytics.comcastTrackingId, 'auto')
      // window.ga('set', 'appId', configuration.analytics.comcastAppId)

      // TODO: Get and set firmware and and model (dimension98)

      let ttsEnabled = false
      try {
        const vgSettings = await Accessibility.voiceGuidanceSettings()
        ttsEnabled = vgSettings.enabled
      } catch (error) {
        console.error('error getting tts', error)
      }
      const ppid = await getSponsorshipInfoComcast()

      adjustZoom()

      const deviceModel = await firebolt.Device.model()
      const distributor = await firebolt.Device.distributor()

      try {
        const ccSettings = await Accessibility.closedCaptionsSettings()
        const isXumo = distributor === 'xglobal'
        captionsOn = ccSettings.enabled ? true : captionsOn
        let mappedStyles = getStylesComcast(ccSettings, isXumo)
        mappedStyles.enabled = captionsOn
        store.dispatch({
          type: 'SET_CAPTION_STATE',
          payload: mappedStyles,
        })
      } catch (error) {
        console.error('error getting cc settings', error)
      }

      store.dispatch({
        type: 'UPDATE_DEVICE',
        payload: { deviceModel, distributor, ttsEnabled, ...ppid },
      })
    })
  } else {
    // window.ga('create', configuration.analytics.samsungTrackingId, 'auto')
    // window.ga('set', 'appId', configuration.analytics.browserAppId)
    //When launching on a browser, enable captions depending on localStorage
    store.dispatch({
      type: 'SET_CAPTION_STATE',
      payload: { enabled: captionsOn },
    })
  }

  // window.ga('set', 'appName', configuration.analytics.appName)
  // eslint-disable-next-line no-undef
  // window.ga('set', 'appVersion', BUILD_NUMBER)
  checkDeviceId()
  tts.pushTextToRead('PBS Video App Launching')

  checkActivationStatus((error) => {
    const container = document.getElementById('root')
    const root = createRoot(container)

    googleAnalytics4.initializeGA4()

    if (error) {
      triggerSentryError(
        'Error checking activation status',
        'index.js',
        {
          errorName: error.name ?? 'An error occurred (default)',
          errorMessage: error.message ?? 'Undefined error message (default)',
        },
        true
      )
      document.addEventListener('keydown', function (e) {
        if (e.keyCode === 13) {
          deleteActivationStatus()
          window.location.href = '/'
        }
      })
      tts.addTextToRead(
        'Error With Activation Press Ok or Enter to reset the app and try again.'
      )
      root.render(
        <div className="error-screen-container ">
          <div className="text-container">
            <h1 className="title">Error With Activation</h1>
            <p className="text">{error.type}</p>
            <p className="text">{JSON.stringify(error.data.message)}</p>
            <p className="text">
              Press Ok or Enter to reset the app and try again.
            </p>
          </div>
        </div>
      )
    } else {
      root.render(
        <Provider store={store}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </Provider>
      )
    }
  })

  document.addEventListener('keydown', onKeyDown(store))
}

document.addEventListener('DOMContentLoaded', main)
